import type { GraphNode } from 'features/knowledgeDocumentViewer/graph/graphTypes'
import type {
  GraphScope,
  GraphTransport,
} from 'features/knowledgeDocumentViewer/graph/loadPageGraph'
import {
  type PnidNodeOverlay,
  pnidNodeOverlaySchema,
} from 'features/knowledgeDocumentViewer/graph/pnidSchemas'
import {
  type SystemDocumentPageEntry,
  SystemDocumentPageEntrySchema,
} from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { z } from 'zod'

const pageDestinationSchema = z.object({
  documentId: z.string().min(1),
  pageNumber: z.number().int().positive(),
})
export type PageDestination = z.infer<typeof pageDestinationSchema>

const overlayResponseSchema = z.array(
  z.looseObject({ entry: pnidNodeOverlaySchema })
)

function nodeScope(tags: GraphNode['tags']): GraphScope {
  const pipeline = tags['pipeline-config']
  if (!pipeline || tags.sandbox !== 'sandbox')
    throw new Error('Node scope is missing')
  return {
    organization: tags.organization,
    site: tags.site,
    'pipeline-config': pipeline,
  }
}

async function loadNodeOverlays(
  nodeId: string,
  scope: GraphScope,
  load: GraphTransport
): Promise<PnidNodeOverlay[]> {
  const rows = overlayResponseSchema.parse(
    await load({
      type: ['pnid-node-overlay'],
      tags: { ...scope, sandbox: 'sandbox' },
      filters: [{ field: 'data', key: 'node_id', op: 'in', value: [nodeId] }],
      limit: null,
    })
  )
  for (const { entry } of rows)
    if (
      entry.data.node_id !== nodeId ||
      entry.data.page_id !== entry.tags['system-document-page'] ||
      !entry.data.page_id ||
      entry.tags.organization !== scope.organization ||
      entry.tags.site !== scope.site ||
      entry.tags['pipeline-config'] !== scope['pipeline-config']
    )
      throw new Error('Node appearance scope does not match')
  return [...new Map(rows.map(({ entry }) => [entry.id, entry])).values()]
}

function toPageDestination(
  entry: SystemDocumentPageEntry['entry'] | undefined,
  pageId: string,
  pageOverlays: PnidNodeOverlay[],
  scope: GraphScope
): PageDestination {
  if (
    !entry ||
    entry.id !== pageId ||
    Object.entries(scope).some(([key, value]) => entry.tags?.[key] !== value) ||
    pageOverlays.some(
      (overlay) =>
        overlay.tags['system-document'] &&
        overlay.tags['system-document'] !== entry.tags?.['system-document']
    )
  )
    throw new Error('Node page scope does not match')
  const destination = pageDestinationSchema.parse({
    documentId: entry.data.document_id,
    pageNumber: entry.data.page_num,
  })
  if (
    entry.data.page_id !==
    `${destination.documentId}_pg_${destination.pageNumber}`
  )
    throw new Error('Node page identity does not match')
  return destination
}

async function loadPageDestinations(
  overlays: PnidNodeOverlay[],
  scope: GraphScope,
  load: GraphTransport
): Promise<PageDestination[]> {
  const pageIds = [...new Set(overlays.map((overlay) => overlay.data.page_id))]
  if (pageIds.length === 0) return []
  const rows = z.array(SystemDocumentPageEntrySchema).parse(
    await load({
      type: ['system-document-page'],
      tags: scope,
      ids: pageIds,
      limit: null,
    })
  )
  if (rows.length !== pageIds.length)
    throw new Error('Node pages were not uniquely resolved')
  const entriesById = new Map(rows.map(({ entry }) => [entry.id, entry]))
  return pageIds.map((pageId) =>
    toPageDestination(
      entriesById.get(pageId),
      pageId,
      overlays.filter((overlay) => overlay.data.page_id === pageId),
      scope
    )
  )
}

export async function loadNodePageDestinations(
  nodeId: string,
  tags: GraphNode['tags'],
  load: GraphTransport
): Promise<PageDestination[]> {
  const scope = nodeScope(tags)
  const overlays = await loadNodeOverlays(nodeId, scope, load)
  return loadPageDestinations(overlays, scope, load)
}
